import { useMemo } from "react";
import type { ReactNode } from "react";
import { PublisherGamesListWithPagination } from "../PublisherGamesListWithPagination";
import { PublisherTopSellersListWithPagination } from "../PublisherTopSellersListWithPagination";
import { PublisherGameFollowersListWithPagination } from "../PublisherGameFollowersListWithPagination";
import { PublisherGameWishlistListWithPagination } from "../PublisherGameWishlistListWithPagination";
import { PublisherEventsListWithPagination } from "../PublisherEventsListWithPagination";
import { PublisherFollowersHistory } from "../PublisherFollowersHistory";
import { PublisherCCUHistory } from "../PublisherCCUHistory";
import { PublisherGamesFilters } from "../PublisherGamesFilters";
import { PublisherTopSellersFilters } from "../PublisherTopSellersFilters";
import { PublisherGameWishlistFilters } from "../PublisherGameWishlistFilters";
import { PublisherEventsFilters } from "../PublisherEventsFilters";
import type {
  PublisherGame,
  PublisherTopSellerGame,
  PublisherGameFollowers,
  PublisherGameWishlist,
  PublisherEventFeedItem,
} from "../../types";
import type { PublisherGameType, EventType } from "../../config";
import type { PublisherGameFollowersSort } from "../../api/getPublisherGameFollowers";
import type { PublisherGamesSort } from "../../utils/publisherGamesSort";

type Tab = {
  id: string;
  label: string;
  content: ReactNode;
};

interface UsePublisherTabsProps {
  initialGames: PublisherGame[];
  initialTotal: number;
  initialTopSellers: PublisherTopSellerGame[];
  initialTopSellersTotal: number;
  initialGameFollowers: PublisherGameFollowers[];
  initialGameFollowersTotal: number;
  initialGameWishlist: PublisherGameWishlist[];
  initialGameWishlistTotal: number;
  initialEvents: PublisherEventFeedItem[];
  initialEventsTotal: number;
  pageSize: number;
  clanid: number;
  type: PublisherGameType;
  comingSoon: boolean;
  cc: string;
  followersSort: PublisherGameFollowersSort;
  gamesSort: PublisherGamesSort;
  eventType: EventType;
  gameFilter: number | null;
  filteredGameName: string | null;
}

export function usePublisherTabs({
  initialGames,
  initialTotal,
  initialTopSellers,
  initialTopSellersTotal,
  initialGameFollowers,
  initialGameFollowersTotal,
  initialGameWishlist,
  initialGameWishlistTotal,
  initialEvents,
  initialEventsTotal,
  pageSize,
  clanid,
  type,
  comingSoon,
  cc,
  followersSort,
  gamesSort,
  eventType,
  gameFilter,
  filteredGameName,
}: UsePublisherTabsProps): Tab[] {
  return useMemo(
    () => [
      {
        id: "games",
        label: "Games",
        content: (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
                All games published by this publisher, including released and
                upcoming titles.
              </p>
              <PublisherGamesFilters type={type} comingSoon={comingSoon} />
            </div>
            <PublisherGamesListWithPagination
              key={`${type}-${comingSoon}-${gamesSort}`}
              initialGames={initialGames}
              initialPage={1}
              initialTotal={initialTotal}
              pageSize={pageSize}
              clanid={clanid}
              type={type === "all" ? null : type}
              comingSoon={comingSoon}
              initialSort={gamesSort}
            />
          </div>
        ),
      },
      {
        id: "topsellers",
        label: "Top Sellers",
        content: (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
                Games ranked by sales performance in Steam&apos;s Top Sellers
                list, sorted by their current rank.
              </p>
              <PublisherTopSellersFilters type={type} cc={cc} />
            </div>
            <PublisherTopSellersListWithPagination
              key={`topsellers-${type}-${cc}`}
              initialGames={initialTopSellers}
              initialPage={1}
              initialTotal={initialTopSellersTotal}
              pageSize={pageSize}
              clanid={clanid}
              type={type === "all" ? null : type}
              comingSoon={false}
              cc={cc}
            />
          </div>
        ),
      },
      {
        id: "followers",
        label: "Games Followers",
        content: (
          <div className="space-y-6">
            <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
              Games sorted by follower count with daily, weekly, and monthly
              change indicators.
            </p>
            <PublisherGameFollowersListWithPagination
              initialGames={initialGameFollowers}
              initialPage={1}
              initialTotal={initialGameFollowersTotal}
              pageSize={pageSize}
              clanid={clanid}
              initialSort={followersSort}
            />
          </div>
        ),
      },
      {
        id: "wishlist",
        label: "Top Wishlist",
        content: (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
                Games ranked by their position in Steam&apos;s Top Wishlist,
                showing rank changes over time.
              </p>
              <PublisherGameWishlistFilters type={type} />
            </div>
            <PublisherGameWishlistListWithPagination
              key={`wishlist-${type}`}
              initialGames={initialGameWishlist}
              initialPage={1}
              initialTotal={initialGameWishlistTotal}
              pageSize={pageSize}
              clanid={clanid}
              type={type === "all" ? null : type}
            />
          </div>
        ),
      },
      {
        id: "events",
        label: "Events",
        content: (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
                Unified events feed (wishlist, CCU, Top Sellers)
              </p>
              <PublisherEventsFilters eventType={eventType} />
            </div>
            <PublisherEventsListWithPagination
              key={`events-${eventType}-${gameFilter ?? "all"}`}
              initialEvents={initialEvents}
              initialPage={1}
              initialTotal={initialEventsTotal}
              pageSize={pageSize}
              clanid={clanid}
              eventType={eventType}
              gameFilter={gameFilter}
              filteredGameName={filteredGameName}
            />
          </div>
        ),
      },
      {
        id: "followers-history",
        label: "Followers History",
        content: (
          <div className="space-y-6">
            <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
              Historical chart showing how the total follower count for this
              publisher&apos;s games has changed over time.
            </p>
            <PublisherFollowersHistory clanid={clanid} />
          </div>
        ),
      },
      {
        id: "ccu-history",
        label: "Total Players",
        content: (
          <div className="space-y-6">
            <p className="inline-block bg-[#16202d]/80 backdrop-blur-sm rounded-lg border border-[#2a475e] px-3 py-2 text-sm text-[#8f98a0]">
              Total players history (sum of daily average CCU across all games).
            </p>
            <PublisherCCUHistory clanid={clanid} />
          </div>
        ),
      },
    ],
    [
      initialGames,
      initialTotal,
      initialTopSellers,
      initialTopSellersTotal,
      initialGameFollowers,
      initialGameFollowersTotal,
      initialGameWishlist,
      initialGameWishlistTotal,
      initialEvents,
      initialEventsTotal,
      pageSize,
      clanid,
      type,
      comingSoon,
      cc,
      followersSort,
      gamesSort,
      eventType,
      gameFilter,
      filteredGameName,
    ]
  );
}
